import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';

const STORAGE_KEY = 'edcMode';
const BUBBLES_STORAGE_KEY = 'edcBubblesEnabled';

const EdcContext = createContext({
  edcMode: false,
  melting: false,
  bubblesEnabled: true,
  toggleEdc: () => {},
  toggleBubbles: () => {},
  // Kept as an alias for backwards compatibility — routes through toggleEdc.
  exitEdc: () => {},
});

export function EdcProvider({ children }) {
  const [edcMode, setEdcMode] = useState(false);
  const [melting, setMelting] = useState(false);
  const [bubblesEnabled, setBubblesEnabled] = useState(true);
  // Track in-flight melt timers so a rapid second toggle can supersede them.
  const meltTimersRef = useRef([]);
  // Captured once on mount so we can restore whatever the static HTML shipped
  // with — keeps this decoupled from the specific brand color in index.html.
  const originalThemeColorRef = useRef(null);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') {
        setEdcMode(true);
      }
    } catch (e) {
      // ignore (private mode etc.)
    }
    try {
      // Treat only the explicit "0" as off; anything else (including a missing
      // key) keeps the default-on behavior.
      if (localStorage.getItem(BUBBLES_STORAGE_KEY) === '0') {
        setBubblesEnabled(false);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (edcMode) {
      root.classList.add('edc-mode');
    } else {
      root.classList.remove('edc-mode');
    }
  }, [edcMode]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    if (originalThemeColorRef.current === null) {
      originalThemeColorRef.current = meta.getAttribute('content') || '#ffffff';
    }
    meta.setAttribute(
      'content',
      edcMode ? '#0a0010' : originalThemeColorRef.current,
    );
  }, [edcMode]);

  useEffect(() => () => {
    meltTimersRef.current.forEach((id) => window.clearTimeout(id));
    meltTimersRef.current = [];
  }, []);

  const clearMeltTimers = useCallback(() => {
    meltTimersRef.current.forEach((id) => window.clearTimeout(id));
    meltTimersRef.current = [];
  }, []);

  const toggleEdc = useCallback(() => {
    clearMeltTimers();
    setMelting(true);
    if (!edcMode) {
      try {
        localStorage.setItem(STORAGE_KEY, '1');
      } catch (e) {
        // ignore
      }
      // Reveal EDC mode mid-melt so the new theme paints in behind the
      // overlay.
      meltTimersRef.current.push(
        window.setTimeout(() => setEdcMode(true), 1400),
        window.setTimeout(() => setMelting(false), 3600),
      );
    } else {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore
      }
      // Reverse drip-out: overlay reappears, we flip out of EDC mid-melt so
      // the original theme paints in behind the drips.
      meltTimersRef.current.push(
        window.setTimeout(() => setEdcMode(false), 1400),
        window.setTimeout(() => setMelting(false), 3600),
      );
    }
  }, [edcMode, clearMeltTimers]);

  const toggleBubbles = useCallback(() => {
    setBubblesEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(BUBBLES_STORAGE_KEY, next ? '1' : '0');
      } catch (e) {
        // ignore
      }
      return next;
    });
  }, []);

  return (
    <EdcContext.Provider
      value={{
        edcMode,
        melting,
        bubblesEnabled,
        toggleEdc,
        toggleBubbles,
        exitEdc: toggleEdc,
      }}
    >
      {children}
    </EdcContext.Provider>
  );
}

export function useEdc() {
  return useContext(EdcContext);
}
